import React, { useState, useEffect } from 'react';
import { useDarkMode } from '../../../context/DarkModeContext';
import { useQuarterlyPriorities } from '../../../hooks/useQuarterlyPriorities';
import PriorityItem from './PriorityItem';
import { PlusIcon, SparklesIcon, CalendarIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';

interface QuarterlyPrioritiesProps {
  focusId: string;
  focus?: any;
  currentPhase: string;
  onQuickAdd: (context?: any) => void;
}

const QuarterlyPriorities: React.FC<QuarterlyPrioritiesProps> = ({
  focusId,
  focus,
  currentPhase,
  onQuickAdd
}) => {
  const { darkMode } = useDarkMode();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPriorityTitle, setNewPriorityTitle] = useState('');
  const [newPriorityDescription, setNewPriorityDescription] = useState('');
  const [hasCheckedForPriorities, setHasCheckedForPriorities] = useState(false);
  
  const {
    priorities,
    loading,
    error,
    currentQuarter,
    suggestions,
    loadingSuggestions,
    addPriority,
    toggleComplete,
    updatePriority,
    deletePriority,
    fetchSuggestions
  } = useQuarterlyPriorities(focusId, focus);
  
  const cardBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const inputBg = darkMode ? 'bg-gray-700' : 'bg-gray-50';

  // State for showing suggestions
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Filter suggestions to exclude those that match existing active priorities
  const activePriorityTitles = priorities
    .filter(p => !p.is_completed)
    .map(p => p.priority_title.toLowerCase().trim());
  
  const availableSuggestions = suggestions.filter(suggestion => 
    !activePriorityTitles.includes(suggestion.title.toLowerCase().trim())
  );
  
  const hasAvailableSuggestions = availableSuggestions.length > 0;
  
  // Fetch suggestions on mount to check availability
  useEffect(() => {
    if (!loadingSuggestions && suggestions.length === 0) {
      fetchSuggestions();
    }
  }, []);

  const handleAddPriority = async () => {
    if (!newPriorityTitle.trim()) return;
    
    const result = await addPriority(
      newPriorityTitle.trim(),
      newPriorityDescription.trim() || undefined
    );
    
    if (result) {
      setNewPriorityTitle('');
      setNewPriorityDescription('');
      setShowAddForm(false);
    }
  };

  const handleEditPriority = async (id: string, title: string, description?: string) => {
    await updatePriority(id, { priority_title: title, priority_description: description });
  };

  const handleDeletePriority = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this priority?')) {
      await deletePriority(id);
    }
  };

  // Get phase name for display
  const getPhaseDisplay = (phase: string) => {
    const phaseMap: Record<string, string> = {
      planning: 'Planning',
      foundation: 'Foundation',
      execution: 'Execution',
      impact: 'Impact',
      review_prep: 'Review Preparation'
    };
    return phaseMap[phase] || phase;
  };

  // Get quarter display (e.g., "2025-Q1" -> "Q1 2025")
  const getQuarterDisplay = (quarter: string) => {
    const [year, q] = quarter.split('-');
    return `${q} ${year}`;
  };

  if (loading) {
    return (
      <div className={`${cardBg} ${borderColor} border rounded-lg p-6`}>
        <div className="animate-pulse">
          <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${cardBg} ${borderColor} border rounded-lg p-6`}>
        <p className="text-red-500">Failed to load priorities</p>
      </div>
    );
  }

  const incompletePriorities = priorities.filter(p => !p.is_completed);
  const completedPriorities = priorities.filter(p => p.is_completed);

  return (
    <div className={`${cardBg} ${borderColor} border rounded-lg p-6`}>
      <div className="flex justify-between items-start mb-4">
        <div>
          <h2 
            className={`text-lg font-semibold ${textColor} inline-flex items-center group`}
            title="Set 3-5 high-level objectives for this quarter that align with your performance goals and team priorities"
          >
            Quarterly Priorities
            <span className={`ml-2 text-xs ${mutedTextColor} opacity-0 group-hover:opacity-100 transition-opacity`}>
              ⓘ
            </span>
          </h2>
          <p className={`text-sm ${mutedTextColor} mt-1`}>
            {getQuarterDisplay(currentQuarter)} • {getPhaseDisplay(currentPhase)} Phase
          </p>
        </div>
        <div className="flex items-center gap-2">
          {priorities.length === 0 && hasAvailableSuggestions && (
            <button
              onClick={() => {
                fetchSuggestions();
                setShowSuggestions(true);
              }}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
            >
              <SparklesIcon className="w-4 h-4" />
              View Suggestions
            </button>
          )}
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300"
          >
            <PlusIcon className="w-4 h-4" />
            Add Priority
          </button>
        </div>
      </div>

      {/* Add Priority Form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-4 p-3 rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20"
          >
            <p className={`text-xs ${mutedTextColor} mb-2`}>
              Set 3-5 high-level objectives for this quarter that align with your performance goals and team priorities
            </p>
            <input
              type="text"
              value={newPriorityTitle}
              onChange={(e) => setNewPriorityTitle(e.target.value)}
              placeholder="What needs to be accomplished?"
              className={`w-full px-3 py-2 rounded-lg border ${
                darkMode 
                  ? 'bg-gray-800 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              } focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleAddPriority();
                }
                if (e.key === 'Escape') {
                  setShowAddForm(false);
                  setNewPriorityTitle('');
                  setNewPriorityDescription('');
                }
              }}
            />
            <textarea
              value={newPriorityDescription}
              onChange={(e) => setNewPriorityDescription(e.target.value)}
              placeholder="Description (optional)"
              className={`w-full mt-2 px-3 py-2 rounded-lg border ${
                darkMode 
                  ? 'bg-gray-800 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              } focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
              rows={2}
            />
            <div className="flex justify-between items-center mt-2">
              {hasAvailableSuggestions && (
                <button
                  onClick={() => {
                    fetchSuggestions();
                    setShowSuggestions(true);
                    setShowAddForm(false);
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300"
                >
                  <SparklesIcon className="w-4 h-4" />
                  View Suggestions
                </button>
              )}
              <div className={`flex gap-2 ${!hasAvailableSuggestions ? 'ml-auto' : ''}`}>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setNewPriorityTitle('');
                    setNewPriorityDescription('');
                  }}
                  className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddPriority}
                  disabled={!newPriorityTitle.trim()}
                  className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add Priority
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Priority Lists */}
      <div className="space-y-4">
        {/* Incomplete Priorities */}
        {incompletePriorities.length > 0 && (
          <div className="space-y-2">
            <AnimatePresence>
              {incompletePriorities.map((priority) => (
                <PriorityItem
                  key={priority.id}
                  priority={priority}
                  onToggleComplete={toggleComplete}
                  onEdit={handleEditPriority}
                  onDelete={handleDeletePriority}
                />
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Completed Priorities */}
        {completedPriorities.length > 0 && (
          <div className="space-y-2">
            <h3 className={`text-sm font-medium ${mutedTextColor} mb-2`}>
              Completed ({completedPriorities.length})
            </h3>
            <AnimatePresence>
              {completedPriorities.map((priority) => (
                <PriorityItem
                  key={priority.id}
                  priority={priority}
                  onToggleComplete={toggleComplete}
                />
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Suggestions */}
        {showSuggestions && availableSuggestions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-4 p-4 rounded-lg bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-800"
          >
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-sm font-medium text-purple-900 dark:text-purple-100">
                Suggested Priorities for {getPhaseDisplay(currentPhase)} Phase
              </h4>
              <button
                onClick={() => setShowSuggestions(false)}
                className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-2">
              {loadingSuggestions ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-purple-500"></div>
                </div>
              ) : (
                availableSuggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 rounded-lg bg-white dark:bg-gray-800 border border-purple-100 dark:border-purple-900"
                  >
                    <div className="flex items-start space-x-3">
                      <span className="text-lg mt-0.5">{suggestion.icon}</span>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {suggestion.title}
                        </p>
                        {suggestion.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                            {suggestion.description}
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setNewPriorityTitle(suggestion.title);
                        setNewPriorityDescription(suggestion.description || '');
                        setShowAddForm(true);
                        setShowSuggestions(false);
                      }}
                      className="px-3 py-1 text-sm bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50"
                    >
                      Use This
                    </button>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}

        {/* Empty State */}
        {priorities.length === 0 && !showSuggestions && (
          <div className="text-center py-8">
            <CalendarIcon className={`w-12 h-12 mx-auto ${mutedTextColor} mb-3`} />
            <p className={`${mutedTextColor} mb-4`}>
              No priorities set for this quarter yet
            </p>
            <div className="space-y-2">
              {hasAvailableSuggestions ? (
                <>
                  <button
                    onClick={() => {
                      fetchSuggestions();
                      setShowSuggestions(true);
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    <SparklesIcon className="w-5 h-5" />
                    View Suggested Priorities
                  </button>
                  <p className={`text-xs ${mutedTextColor}`}>
                    or add your own priorities using the button above
                  </p>
                </>
              ) : (
                <p className={`text-sm ${mutedTextColor}`}>
                  Add your own priorities using the button above
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      {priorities.length > 0 && (
        <div className={`mt-4 pt-4 border-t ${borderColor}`}>
          <div className="text-sm">
            <span className={mutedTextColor}>
              {incompletePriorities.length} active • {completedPriorities.length} completed
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuarterlyPriorities;